import {
  absoluteMin,
  BlockStatus,
  dayBounds,
  findOverlaps,
  formatMin,
  freeGaps,
  MINUTES_PER_DAY,
  mergeForDisplay,
  normalizeBlockTitle,
  resolveSegments,
  snapUp,
  type LiveTimeBlock,
  type Span,
} from '@wiztime/core';

/** Pure view-model helpers of the day timeline: positions, groups, gaps, conflicts, axis labels. */

/** Blocks shorter than this many minutes show only a truncated name (requirements §5.2). */
export const COMPACT_BLOCK_MIN = 30;
/** A lane is the 328px day column, or one of the narrow equal-width columns of the week view. */
export const LaneVariant = { Day: 'day', Week: 'week' } as const;
export type LaneVariant = (typeof LaneVariant)[keyof typeof LaneVariant];

/** Vertical position of a wall minute inside the lane, as a percentage of the lane height. */
export function percentOf(min: number, dayStartMin: number): number {
  return ((min - dayStartMin) / MINUTES_PER_DAY) * 100;
}

/** A block, or several touching blocks of the same project, status and title shown as one. */
export interface DisplayGroup {
  /** Id of the first member; stable while the group's first block is unchanged. */
  readonly key: string;
  readonly memberIds: readonly string[];
  readonly members: readonly LiveTimeBlock[];
  readonly startMin: number;
  readonly endMin: number;
  readonly projectId: string;
  readonly status: BlockStatus;
  /** Normalized shared block title, or null for untitled blocks (2026-10b §5.1). */
  readonly title: string | null;
}

function toDisplayGroup(
  memberIds: readonly string[],
  byId: ReadonlyMap<string, LiveTimeBlock>,
  span: Span & { projectId: string; status: BlockStatus; title?: string | null },
): DisplayGroup {
  const members = memberIds
    .map((id) => byId.get(id))
    .filter((member): member is LiveTimeBlock => member !== undefined);
  return {
    key: memberIds[0] ?? '',
    memberIds,
    members,
    startMin: span.startMin,
    endMin: span.endMin,
    projectId: span.projectId,
    status: span.status,
    title: normalizeBlockTitle(span.title),
  };
}

/**
 * Groups for display: touching blocks of one project and status merge (`mergeForDisplay`);
 * blocks that conflict with another block never merge.
 */
export function buildGroups(
  blocks: readonly LiveTimeBlock[],
  conflictIds: ReadonlySet<string>,
): DisplayGroup[] {
  const byId = new Map(blocks.map((block) => [block.id, block]));
  const flat = (block: LiveTimeBlock) => ({
    id: block.id,
    startMin: block.data.startMin,
    endMin: block.data.endMin,
    projectId: block.data.projectId,
    status: block.data.status,
    // The display grouping key includes the normalized title (2026-10b §5.1): adjacent
    // same-project blocks with different titles never merge.
    title: block.data.title ?? null,
  });
  const mergeable = blocks.filter((block) => !conflictIds.has(block.id)).map(flat);
  const merged = mergeForDisplay(mergeable).map((group) => toDisplayGroup(group.memberIds, byId, group));
  const single = blocks
    .filter((block) => conflictIds.has(block.id))
    .map((block) => toDisplayGroup([block.id], byId, flat(block)));
  return [...merged, ...single].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin || (a.key < b.key ? -1 : 1),
  );
}

/** Narrowest side-by-side overlap column worth creating, in pixels (ui-redesign §3.1.4). */
export const OVERLAP_COLUMN_MIN_PX = 200;
/** Overlapping blocks always get at least two side-by-side columns. */
export const MIN_OVERLAP_COLUMNS = 2;
/** And never more than four; further blocks are stacked on top. */
export const MAX_OVERLAP_COLUMNS = 4;
/** An overlap column narrower than this shows no text, only its colour (ui-redesign §3.1.4). */
export const MIN_OVERLAP_TEXT_COLUMN_PX = 56;

/** Width of the create band of a day lane in pixels: `clamp(16px, 6cqi, 40px)` of the stylesheet. */
export function createBandPx(laneWidthPx: number, variant: LaneVariant): number {
  if (variant === LaneVariant.Week) return 12;
  return Math.min(40, Math.max(16, laneWidthPx * 0.06));
}

/**
 * How many side-by-side columns an overlap may use in a lane of this width (ui-redesign
 * §3.1.4): `clamp(floor(available / 200px), 2, 4)` where available = lane width - create band.
 * The week lanes always use two. An unmeasured lane (width 0) gets the minimum.
 */
export function overlapColumnCap(laneWidthPx: number, variant: LaneVariant = LaneVariant.Day): number {
  if (variant === LaneVariant.Week) return MIN_OVERLAP_COLUMNS;
  const available = laneWidthPx - createBandPx(laneWidthPx, variant);
  return Math.min(
    MAX_OVERLAP_COLUMNS,
    Math.max(MIN_OVERLAP_COLUMNS, Math.floor(available / OVERLAP_COLUMN_MIN_PX)),
  );
}

export interface ConflictLayout {
  /** Zero-based column among the side-by-side overlapping blocks. */
  readonly column: number;
  /** Side-by-side columns of the whole overlap group (2 to the lane's cap). */
  readonly columns: number;
  /** How many consecutive columns this block widens over (at least 1, ui-redesign §3.1.4). */
  readonly span: number;
  /** Position in the pile of blocks that did not get a column of their own: 0 for blocks that did. */
  readonly stack: number;
}

export interface Conflicts {
  /** Overlapping pairs of confirmed blocks: these carry the overlap marker and the popover. */
  readonly pairs: ReadonlyArray<readonly [string, string]>;
  /** Every block that overlaps another (drafts included): these never merge into display groups. */
  readonly ids: ReadonlySet<string>;
  /** Confirmed blocks that overlap another confirmed block: they show the overlap marker. */
  readonly marked: ReadonlySet<string>;
  readonly layout: ReadonlyMap<string, ConflictLayout>;
}

/**
 * Overlapping blocks and the side-by-side column layout that keeps them readable (ui-redesign
 * §3.1.4). Per overlap group (connected component), blocks are placed first-fit in start-time
 * order (ties by id - never by countRank, so changing the attribution does not move blocks),
 * then each block widens rightwards over consecutive free columns. At most `maxColumns`
 * columns; further blocks pile on the last column, the greater `(countRank, id)` on top.
 */
export function findConflicts(
  blocks: readonly LiveTimeBlock[],
  maxColumns: number = MIN_OVERLAP_COLUMNS,
): Conflicts {
  const byId = new Map(blocks.map((block) => [block.id, block]));
  const spans = blocks.map((block) => ({
    id: block.id,
    startMin: block.data.startMin,
    endMin: block.data.endMin,
  }));
  const allPairs = findOverlaps(spans);
  const ids = new Set(allPairs.flat());
  const confirmed = (id: string): boolean => byId.get(id)?.data.status === BlockStatus.Confirmed;
  const pairs = allPairs.filter(([a, b]) => confirmed(a) && confirmed(b));
  const marked = new Set(pairs.flat());

  // Connected components of the overlap graph share one column count.
  const parent = new Map<string, string>([...ids].map((id) => [id, id]));
  const find = (id: string): string => {
    const up = parent.get(id) ?? id;
    return up === id ? id : find(up);
  };
  for (const [a, b] of allPairs) parent.set(find(a), find(b));

  const members = spans
    .filter((span) => ids.has(span.id))
    .sort((a, b) => a.startMin - b.startMin || (a.id < b.id ? -1 : 1));
  const byRoot = new Map<string, typeof members>();
  for (const span of members) {
    const root = find(span.id);
    byRoot.set(root, [...(byRoot.get(root) ?? []), span]);
  }

  const layout = new Map<string, ConflictLayout>();
  for (const group of byRoot.values()) {
    // First-fit into the first column that is free at the block's start.
    const rawColumn = new Map<string, number>();
    const columnEnds: number[] = [];
    for (const span of group) {
      const free = columnEnds.findIndex((end) => end <= span.startMin);
      const index = free === -1 ? columnEnds.length : free;
      columnEnds[index] = span.endMin;
      rawColumn.set(span.id, index);
    }
    const columns = Math.min(columnEnds.length, maxColumns);

    // Blocks beyond the cap pile on the last column; greater (countRank, id) lies on top.
    const rank = (id: string): number => byId.get(id)?.data.countRank ?? 0;
    const piled = group
      .filter((span) => (rawColumn.get(span.id) ?? 0) >= maxColumns)
      .sort((a, b) => rank(a.id) - rank(b.id) || (a.id < b.id ? -1 : 1));
    const stack = new Map(piled.map((span, index) => [span.id, index + 1]));

    // Occupancy per clamped column, for the rightward widening.
    const occupied: Span[][] = Array.from({ length: columns }, () => []);
    for (const span of group) {
      const column = Math.min(rawColumn.get(span.id) ?? 0, columns - 1);
      occupied[column]?.push(span);
    }
    for (const span of group) {
      const column = Math.min(rawColumn.get(span.id) ?? 0, columns - 1);
      // Widen over consecutive free columns: no other block in them during this block's span.
      let width = 1;
      for (let next = column + 1; next < columns; next += 1) {
        const busy = occupied[next]?.some(
          (other) => other !== span && other.startMin < span.endMin && other.endMin > span.startMin,
        );
        if (busy === true) break;
        width += 1;
      }
      layout.set(span.id, { column, columns, span: width, stack: stack.get(span.id) ?? 0 });
    }
  }
  return { pairs, ids, marked, layout };
}

/**
 * The stretches of each confirmed block whose minutes the union-based statistics attribute to
 * another confirmed block (ui-redesign §2.1.5): they are drawn with the 135° hatch. Derived
 * from `resolveSegments`, so the hatch always matches what the statistics count. Drafts are
 * never counted, so they neither lose nor take minutes. Keys are block ids; spans are wall
 * minutes of the given personal date, ordered by time; blocks that lost nothing are absent.
 */
export function stolenSpans(
  blocks: readonly LiveTimeBlock[],
  date: string,
): ReadonlyMap<string, readonly Span[]> {
  const base = absoluteMin(date, 0);
  const own = new Map<string, Span[]>();
  for (const segment of resolveSegments(blocks)) {
    const spans = own.get(segment.blockId) ?? [];
    spans.push({ startMin: segment.startAbs - base, endMin: segment.endAbs - base });
    own.set(segment.blockId, spans);
  }
  const stolen = new Map<string, readonly Span[]>();
  for (const block of blocks) {
    if (block.data.status !== BlockStatus.Confirmed) continue;
    const kept = own.get(block.id) ?? [];
    const lost: Span[] = [];
    let cursor = block.data.startMin;
    for (const span of kept) {
      if (span.startMin > cursor) lost.push({ startMin: cursor, endMin: span.startMin });
      cursor = Math.max(cursor, span.endMin);
    }
    if (cursor < block.data.endMin) lost.push({ startMin: cursor, endMin: block.data.endMin });
    if (lost.length > 0) stolen.set(block.id, lost);
  }
  return stolen;
}

/**
 * The "+ Log" slots: every free gap cut into pieces of the default duration. A last piece
 * shorter than the minimum duration merges into the previous piece of its gap; a whole gap
 * shorter than the minimum duration stays one slot of its own — it is kept, not dropped
 * (2026-10b §3.3, keyboard and screen-reader reach every free stretch).
 */
export function gapSlots(
  blocks: readonly Span[],
  dayStartMin: number,
  defaultDurationMin: number,
  stepMin: number,
  minDurationMin: number,
): Span[] {
  const length = Math.max(snapUp(defaultDurationMin, stepMin), stepMin, minDurationMin);
  const slots: Span[] = [];
  for (const gap of freeGaps(blocks, dayBounds(dayStartMin))) {
    for (let start = gap.startMin; start < gap.endMin; start += length) {
      const end = Math.min(start + length, gap.endMin);
      const previous = slots[slots.length - 1];
      if (end - start < minDurationMin && previous !== undefined && previous.endMin === start) {
        slots[slots.length - 1] = { startMin: previous.startMin, endMin: end };
      } else {
        slots.push({ startMin: start, endMin: end });
      }
    }
  }
  return slots;
}

/** Parts of the sleep range that fall inside the personal day (the range may wrap by 24 h). */
export function sleepBands(sleepStartMin: number, sleepEndMin: number, dayStartMin: number): Span[] {
  const bounds = dayBounds(dayStartMin);
  const bands: Span[] = [];
  for (const shift of [-MINUTES_PER_DAY, 0, MINUTES_PER_DAY]) {
    const startMin = Math.max(sleepStartMin + shift, bounds.startMin);
    const endMin = Math.min(sleepEndMin + shift, bounds.endMin);
    if (endMin > startMin) bands.push({ startMin, endMin });
  }
  return bands;
}

/** A two-line "next day" label needs this much clearance from its neighbours. */
const NEXT_DAY_CLEARANCE_MIN = 90;

export interface AxisLabel {
  readonly min: number;
  readonly text: string;
  /** True at 00:00 after the personal day crossed midnight: labelled "next day". */
  readonly nextDay: boolean;
}

/**
 * Time labels down the axis: every `stepHours` hours from the day start, plus a label at the
 * midnight crossing (dropping any label closer than 90 minutes to it).
 */
export function axisLabels(dayStartMin: number, stepHours: number): AxisLabel[] {
  const labels: AxisLabel[] = [];
  const midnight = dayStartMin > 0 ? MINUTES_PER_DAY : null;
  for (let hour = 0; hour < 24; hour += stepHours) {
    const min = dayStartMin + hour * 60;
    if (midnight !== null && min !== midnight && Math.abs(min - midnight) < NEXT_DAY_CLEARANCE_MIN) continue;
    labels.push({ min, text: formatMin(min), nextDay: min === midnight });
  }
  if (midnight !== null && !labels.some((label) => label.min === midnight)) {
    labels.push({ min: midnight, text: formatMin(midnight), nextDay: true });
    labels.sort((a, b) => a.min - b.min);
  }
  return labels;
}

/** Label every hour when there is room, otherwise every second hour. */
export function labelStepHours(laneHeightPx: number): number {
  return laneHeightPx / 24 >= 34 ? 1 : 2;
}

/**
 * A block rendered lower than this many pixels has no room for one line of text: it shows only its
 * colour bar and puts the full information in its tooltip.
 */
export const MIN_TEXT_BLOCK_PX = 18;

/** Rendered height of a block: proportional to its duration, 2px smaller than its slot, at least 6px (as in the stylesheet). */
export function blockHeightPx(startMin: number, endMin: number, laneHeightPx: number): number {
  return Math.max(6, ((endMin - startMin) * laneHeightPx) / MINUTES_PER_DAY - 2);
}

export function isTinyBlock(startMin: number, endMin: number, laneHeightPx: number): boolean {
  return blockHeightPx(startMin, endMin, laneHeightPx) < MIN_TEXT_BLOCK_PX;
}

/** A titled block below 32px shows one line (title + project name); from 32px two lines (2026-10b §5.2). */
export const TITLED_TWO_LINE_MIN_PX = 32;
/** From 44px a titled block uses the large (14px) title and may carry the note line (§5.2). */
export const TITLED_TALL_MIN_PX = 44;

/** Single-line rendering of a titled block: under the 32px two-line threshold (2026-10b §5.2). */
export function isTitledSingleLine(startMin: number, endMin: number, laneHeightPx: number): boolean {
  return blockHeightPx(startMin, endMin, laneHeightPx) < TITLED_TWO_LINE_MIN_PX;
}

/** Tall titled block (2026-10b §5.2): 14px title, note line allowed. */
export function isTitledTall(startMin: number, endMin: number, laneHeightPx: number): boolean {
  return blockHeightPx(startMin, endMin, laneHeightPx) >= TITLED_TALL_MIN_PX;
}

export function isCompactBlock(startMin: number, endMin: number, laneHeightPx: number): boolean {
  const heightPx = ((endMin - startMin) / MINUTES_PER_DAY) * laneHeightPx;
  return endMin - startMin < COMPACT_BLOCK_MIN || heightPx < 26;
}

/** A label this close (in pixels) to the "now" chip is hidden: the chip already tells the time. */
export const NOW_CHIP_CLEARANCE_PX = 14;

/** Whether the "now" chip would cover the axis label at `labelMin`. */
export function isCoveredByNowChip(labelMin: number, nowMin: number | null, laneHeightPx: number): boolean {
  if (nowMin === null) return false;
  return (Math.abs(labelMin - nowMin) / MINUTES_PER_DAY) * laneHeightPx < NOW_CHIP_CLEARANCE_PX;
}

/** A block at least this many pixels high may wrap its name onto two lines in the narrow week lanes. */
export const MIN_TWO_LINE_BLOCK_PX = 30;

export function isRoomyBlock(startMin: number, endMin: number, laneHeightPx: number): boolean {
  return blockHeightPx(startMin, endMin, laneHeightPx) >= MIN_TWO_LINE_BLOCK_PX;
}

/** Something in the lane the keyboard can stop at: a block (group) or an empty "+ Log" slot. */
export const LaneItemKind = { Gap: 'gap', Block: 'block' } as const;
export type LaneItemKind = (typeof LaneItemKind)[keyof typeof LaneItemKind];

export interface LaneItem {
  /** Stable identity: `block:<group key>` or `gap:<start minute>`. */
  readonly id: string;
  readonly kind: LaneItemKind;
  readonly startMin: number;
  readonly endMin: number;
}

export const blockItemId = (groupKey: string): string => `block:${groupKey}`;
export const gapItemId = (startMin: number): string => `gap:${startMin}`;

/** The lane's items in start-time order (a block first when a block and a slot start together). */
export function laneItems(groups: readonly DisplayGroup[], slots: readonly Span[]): LaneItem[] {
  const items: LaneItem[] = [
    ...groups.map((group) => ({
      id: blockItemId(group.key),
      kind: LaneItemKind.Block,
      startMin: group.startMin,
      endMin: group.endMin,
    })),
    ...slots.map((slot) => ({
      id: gapItemId(slot.startMin),
      kind: LaneItemKind.Gap,
      startMin: slot.startMin,
      endMin: slot.endMin,
    })),
  ];
  return items.sort(
    (a, b) =>
      a.startMin - b.startMin ||
      (a.kind === b.kind ? 0 : a.kind === LaneItemKind.Block ? -1 : 1) ||
      a.endMin - b.endMin ||
      (a.id < b.id ? -1 : 1),
  );
}

/**
 * The item that holds the lane's single tab stop when the user has not moved focus yet: the first
 * one that has not ended by `nowMin` (a lane showing today), else the first one.
 */
export function defaultLaneItem(items: readonly LaneItem[], nowMin: number | null): string | null {
  const upcoming = nowMin === null ? undefined : items.find((item) => item.endMin > nowMin);
  return (upcoming ?? items[0])?.id ?? null;
}

export const LaneStep = { Previous: 'previous', Next: 'next', First: 'first', Last: 'last' } as const;
export type LaneStep = (typeof LaneStep)[keyof typeof LaneStep];

/** The item reached from `currentId` by `step`; stays on the first or last item at the ends. */
export function stepLaneItem(
  items: readonly LaneItem[],
  currentId: string | null,
  step: LaneStep,
): string | null {
  if (items.length === 0) return null;
  const last = items.length - 1;
  const index = items.findIndex((item) => item.id === currentId);
  const target =
    step === LaneStep.First
      ? 0
      : step === LaneStep.Last
        ? last
        : step === LaneStep.Next
          ? Math.min(last, index + 1)
          : Math.max(0, index === -1 ? 0 : index - 1);
  return items[target]?.id ?? null;
}
